import { clsx } from "clsx";
import { SSOIntegrationsRight } from "core/types";
import { CircleAlertIcon, CircleHelpIcon } from "lucide-react";
import { useTranslations } from "translations";

export type SSODialogContentProps = {
  headerNotUserManager?: string;
  headerUserManager?: string;
  integrations: SSOIntegrationsRight[];
  showContactSupport?: boolean;
  subtitle: string;
};

function SSOIntegrationCard({
  hasAdminRights,
  integration,
}: {
  hasAdminRights?: boolean;
  integration: SSOIntegrationsRight;
}) {
  return (
    <div
      className={clsx("flex items-center rounded-lg p-3", {
        "bg-green-50": hasAdminRights,
        "bg-yellow-50": !hasAdminRights,
      })}
    >
      <div className="flex-1 text-sm font-medium text-gray-900">
        #{integration.careseeker.id} {integration.careseeker.name}
      </div>
    </div>
  );
}

export function SSOHeaderIcon({
  variant = "info",
}: {
  variant?: "info" | "warning";
}) {
  if (variant === "warning") {
    return (
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-warning-light">
        <CircleAlertIcon
          aria-hidden
          className="text-xl font-bold text-warning"
        />
      </span>
    );
  }

  return (
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
      <CircleHelpIcon aria-hidden className="h-8 w-8 text-primary-dark" />
    </span>
  );
}

export function SSODialogContent({
  headerNotUserManager,
  headerUserManager,
  integrations,
  showContactSupport = false,
  subtitle,
}: SSODialogContentProps) {
  const translations = useTranslations();
  const userManagerRights = integrations.filter(
    (integration) => integration.has_admin_rights,
  );
  const nonUserManagerRights = integrations.filter(
    (integration) => !integration.has_admin_rights,
  );

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">{subtitle}</div>
      {nonUserManagerRights.length > 0 && (
        <div className="space-y-2">
          {headerNotUserManager && (
            <div className="font-medium text-gray-900">
              {headerNotUserManager}
            </div>
          )}
          {nonUserManagerRights.map((integration) => (
            <SSOIntegrationCard
              key={integration.sso_integration_id}
              hasAdminRights={false}
              integration={integration}
            />
          ))}
        </div>
      )}
      {userManagerRights.length > 0 && (
        <div className="space-y-2">
          {headerUserManager && (
            <div className="font-medium text-gray-900">{headerUserManager}</div>
          )}
          {userManagerRights.map((integration) => (
            <SSOIntegrationCard
              key={integration.sso_integration_id}
              hasAdminRights={true}
              integration={integration}
            />
          ))}
        </div>
      )}
      {showContactSupport && (
        <div className="text-sm text-gray-600">
          {translations.userManagement.ssoFlow.stepTwo.issueSubtitleTwo}
        </div>
      )}
    </div>
  );
}
